import React, { useState, useCallback, useEffect } from 'react';
import { AppProvider, Frame, Navigation, Page, Card, Text } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import {
  HomeMajor,
  EmailMajor,
  OrdersMajor,
  SettingsMajor,
  CreditCardMajor,
  ColorsMajor,
  ChecklistMajor,
  QuestionMarkMajor,
  ViewMajor,
  LockMajor,
  AnalyticsMajor,
  SearchMajor
} from '@shopify/polaris-icons';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import enTranslations from '@shopify/polaris/locales/en.json';
import { useShopifyNavigation } from './hooks/useShopifyNavigation';
import { AppBridgeProvider } from './components/AppBridgeProvider';
import { createSafeComponent, AppErrorBoundary } from './components/ErrorBoundary';
import { CompactLanguageSwitcher } from './components/LanguageSwitcher';

// Import pages
import Home from './pages/Home';
import EmailJourneys from './pages/EmailJourneys';
import Orders from './pages/Orders';
import OrderDetails from './pages/OrderDetails';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import Personalization from './pages/Personalization';
import GetStarted from './pages/GetStarted';
import EmailTemplatesUnsafe from './pages/EmailTemplates';
import HowItWorks from './pages/HowItWorks';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Wrap critical components with error boundaries
const EmailTemplates = createSafeComponent(EmailTemplatesUnsafe, 'EmailTemplates');

function AppFrame() {
  const navigate = useNavigate();
  const navigateWithContext = useShopifyNavigation();
  const location = useLocation();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(true);
  const { t } = useTranslation();

  // Check if we're in embedded mode or standalone
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const host = urlParams.get('host') || sessionStorage.getItem('shopify_host');
    const shop = urlParams.get('shop') || sessionStorage.getItem('shopify_shop');
    const embedded = urlParams.get('embedded');
    
    // If no Shopify context, we're in standalone mode
    if (!host && !shop && embedded !== '1') {
      setIsEmbedded(false);
    }
  }, []);

  // If not embedded, show a helpful message WITHOUT using Polaris Banner to avoid i18n conflict
  if (!isEmbedded) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          backgroundColor: '#f6f8fa',
          border: '1px solid #d0d7de',
          borderRadius: '8px',
          padding: '20px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h2 style={{ color: '#1f2937', marginBottom: '16px' }}>TrackingMania - Access via Shopify Admin</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>This app is designed to work within Shopify Admin.</p>
          <div style={{ marginTop: '15px' }}>
            <strong style={{ color: '#1f2937' }}>To access TrackingMania:</strong>
          </div>
          <ol style={{ textAlign: 'left', marginTop: '10px', display: 'inline-block', color: '#6b7280' }}>
            <li>Go to your Shopify Admin</li>
            <li>Navigate to Apps</li>
            <li>Click on TrackingMania</li>
          </ol>
        </div>
      </div>
    );
  }

  const toggleMobileNavigationActive = useCallback(
    () => setMobileNavigationActive((active) => !active),
    []
  );

  // Primary navigation items (main features)
  const primaryNavigationItems = [
    {
      url: '/',
      label: t('navigation.dashboard'),
      icon: HomeMajor,
      selected: location.pathname === '/',
    },
    {
      url: '/orders',
      label: t('navigation.orders'),
      icon: OrdersMajor,
      selected: location.pathname.startsWith('/orders'),
    },
    {
      url: '/email-journeys',
      label: t('navigation.emailJourneys'),
      icon: EmailMajor,
      selected: location.pathname === '/email-journeys',
    },
  ];

  // Settings and configuration items
  const settingsNavigationItems = [
    {
      url: '/get-started',
      label: t('navigation.getStarted'),
      icon: ChecklistMajor,
      selected: location.pathname === '/get-started',
    },
    {
      url: '/personalization',
      label: t('navigation.trackingPage'),
      icon: ViewMajor,
      selected: location.pathname === '/personalization',
    },
    {
      url: '/email-templates',
      label: t('navigation.emailTemplates'),
      icon: EmailMajor,
      selected: location.pathname === '/email-templates',
    },
    {
      url: '/settings',
      label: t('navigation.settings'),
      icon: SettingsMajor,
      selected: location.pathname === '/settings',
    },
    {
      url: '/how-it-works',
      label: t('navigation.howItWorks'),
      icon: QuestionMarkMajor,
      selected: location.pathname === '/how-it-works',
    },
  ];

  // Account and billing items
  const accountNavigationItems = [
    {
      url: '/billing',
      label: t('navigation.billing'),
      icon: CreditCardMajor,
      selected: location.pathname === '/billing',
    },
  ];

  const handleNavigationClick = useCallback((url) => {
    navigateWithContext(url);
  }, [navigateWithContext]);

  const logo = {
    width: 124,
    topBarSource: 'https://cdn.shopify.com/s/files/1/0446/6937/files/jaded-pixel-logo-color.svg?6215648040070010999',
    contextualSaveBarSource: 'https://cdn.shopify.com/s/files/1/0446/6937/files/jaded-pixel-logo-gray.svg?6215648040070010999',
    url: '/',
    accessibilityLabel: 'TrackingMania',
  };

  const topBarMarkup = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '0 16px' }}>
      <CompactLanguageSwitcher />
    </div>
  );

  return (
    <Frame
      logo={logo}
      topBar={topBarMarkup}
      navigation={
        <Navigation location={location.pathname}>
          <Navigation.Section
            items={primaryNavigationItems.map((item) => ({
              ...item,
              onClick: () => handleNavigationClick(item.url),
            }))}
          />
          <Navigation.Section
            title={t('navigation.configuration', 'Configuração')}
            items={settingsNavigationItems.map((item) => ({
              ...item,
              onClick: () => handleNavigationClick(item.url),
            }))}
          />
          <Navigation.Section
            title={t('navigation.account', 'Conta')}
            items={accountNavigationItems.map((item) => ({
              ...item,
              onClick: () => handleNavigationClick(item.url),
            }))}
          />
        </Navigation>
      }
      showMobileNavigation={mobileNavigationActive}
      onNavigationToggle={toggleMobileNavigationActive}
    >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/order/:id" element={<OrderDetails />} />
        <Route path="/email-journeys" element={<EmailJourneys />} />
        <Route path="/personalization" element={<Personalization />} />
        <Route path="/email-templates" element={<EmailTemplates />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/pages/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Frame>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <AppProvider i18n={enTranslations}>
        <Router>
          <AppBridgeProvider>
            <AppFrame />
          </AppBridgeProvider>
        </Router>
      </AppProvider>
    </AppErrorBoundary>
  );
}

export default App;